.detail {
  position: relative;
  z-index: 2;
  /* Same zoom-aware sizing rule as HubShell .shell so picking a
   * classroom doesn't change the container width. The 92vw/scale cap
   * keeps the zoomed width inside any window (incl. tall/narrow). */
  width: min(1400px, calc(92vw / var(--ui-scale)));
  margin: 0 auto;
  padding: clamp(20px, 4vw, 40px) clamp(20px, 4vw, 40px) 80px;
  font-family: var(--token-font-ui);
  color: var(--token-text-primary);
  /* Scale the whole settings page (heading, tabs, every config form)
   * with the display. The scroll stage handles extra height on a 720p
   * projector. No-op at 1080p. */
  zoom: var(--ui-scale);
}

/* Heading sits centered at the top, matching the hub's "Good evening,
 * Mike / Welcome to Classy" pattern. */
.heading {
  margin-bottom: 18px;
  text-align: center;
}

/* Action row sits between the heading and the tab strip:
 *   ←Classrooms                          [Activate Classroom] [Archive]
 * Both action pills use the standard .glass-pill styling so the colors
 * and text contrast are guaranteed (white pill, dark text — readable
 * on any theme).
 */
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

/* Left-side action group: back chevron + Settings button sit together,
 * separate from the right-side Start Teaching action. */
.leadActions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.back {
  /* `.glass-pill` from app.css gives the white-pill base; we just gap
   * the chevron + label and keep it left-aligned. */
  padding-left: 14px;
  padding-right: 18px;
}

/* "Settings" button — the graduated old "General" tab. Classroom-level
 * config (which pages are on, etc.) reached from the top bar rather than
 * sitting confusingly inside the per-page tab strip. */
.settingsBtn {
  padding-left: 16px;
  padding-right: 18px;
}

/* Active when the Settings view is showing — an accent ring marks it as
 * the current surface without competing with the orange Start Teaching. */
.settingsBtnActive {
  box-shadow:
    0 0 0 2px var(--token-accent),
    0 8px 18px -8px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.85);
}

.actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Archive/Unarchive footer inside the Settings panel — a quiet, divided
 * "danger zone" at the bottom rather than a prominent top-level action. */
.dangerZone {
  margin-top: 24px;
  padding-top: 18px;
  border-top: 1px solid rgba(8, 18, 12, 0.12);
  display: flex;
  justify-content: flex-end;
}

/* Secondary destructive pill — quieter than the orange Activate but
 * still a real button (Mike's ask). Uses the .glass-pill base, with
 * a softer background so it reads as secondary. */
.archive {
  background: rgba(255, 255, 255, 0.5);
  color: var(--token-text-primary);
  border-color: rgba(8, 18, 12, 0.16);
  font-weight: 600;
}

.archive:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.78);
}

/* Small "CLASSROOM" eyebrow above the (now prominent) classroom name.
 * Quiet label so the name itself carries the page identity. */
.eyebrow {
  margin: 0 0 6px;
  font-family: var(--token-font-ui);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.8);
  text-shadow:
    0 1px 4px rgba(8, 18, 12, 0.55),
    0 2px 10px rgba(8, 18, 12, 0.4);
}

.title {
  margin: 0;
  font-family: var(--token-font-display);
  /* The classroom name is now the page's main heading (the old
   * "Classroom Settings" title is demoted to a small eyebrow). */
  font-size: clamp(32px, 4.4vw, 52px);
  color: #fefdf6;
  /* Pairs with the .app::before gradient scrim — the dark band at
   * the top of the page does the heavy contrast lifting; shadows
   * here just add depth. */
  text-shadow:
    0 2px 4px rgba(8, 18, 12, 0.55),
    0 6px 20px rgba(8, 18, 12, 0.45);
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 0 8px;
  border-radius: 12px;
  transition: background 120ms ease;
}

.title:hover .editGlyph {
  opacity: 1;
}

.editGlyph {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.35);
  font-size: 17px;
  opacity: 0.55;
  transition: opacity 120ms ease;
  flex-shrink: 0;
}

.titleInput {
  margin: 0;
  font-family: var(--token-font-display);
  /* Matches the prominent .title sizing. */
  font-size: clamp(32px, 4.4vw, 52px);
  letter-spacing: 0.5px;
  color: #fefdf6;
  background: rgba(8, 18, 12, 0.35);
  border: 1.5px solid rgba(255, 255, 255, 0.55);
  border-radius: 14px;
  padding: 4px 16px;
  outline: none;
  width: min(640px, 80vw);
  text-shadow:
    0 2px 4px rgba(8, 18, 12, 0.55),
    0 6px 20px rgba(8, 18, 12, 0.45);
}

.titleInput:focus {
  border-color: var(--token-accent);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--token-accent) 30%, transparent);
}

.meta {
  margin: 4px 0 0;
  color: rgba(255, 255, 255, 0.95);
  text-shadow:
    0 1px 4px rgba(8, 18, 12, 0.55),
    0 2px 10px rgba(8, 18, 12, 0.4);
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.sectionGroup {
  margin-bottom: 22px;
}

.sectionHeading {
  font-family: var(--token-font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
  text-shadow: 0 1px 6px rgba(8, 18, 12, 0.5);
  margin: 0 0 8px 10px;
}

.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px;
  margin-bottom: 0;
  border-radius: 999px;
}

.tab {
  flex: 0 0 auto;
  min-height: 36px;
  padding: 6px 16px;
  border-radius: 999px;
  border: none;
  background: transparent;
  /* Match the hub tabs: dark text with a white halo holds contrast on
   * any theme background. Active state inverts to white pill below. */
  color: var(--token-text-primary);
  text-shadow:
    0 1px 0 rgba(255, 255, 255, 0.65),
    0 0 8px rgba(255, 255, 255, 0.4);
  font-family: var(--token-font-ui);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition:
    background 120ms ease,
    color 120ms ease;
}

.tab:hover {
  background: rgba(255, 255, 255, 0.32);
}

.tabActive {
  background: rgba(255, 255, 255, 0.95);
  color: #1f2933;
  text-shadow: none;
}

/* Being dragged to reorder (mouse/trackpad). The Pages picker's ‹/›
 * buttons remain the touch/keyboard reorder path. */
.tabDragging {
  opacity: 0.45;
}

/* Disabled classroom page: greyed in settings (it's hidden in the live nav),
 * still clickable so the teacher can configure it (and re-enable from inside
 * the page panel) while off. Enabled pages read normal/black. */
.tabDisabled {
  color: rgba(120, 130, 140, 0.7);
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.5);
  opacity: 0.7;
}

.tabDisabled.tabActive {
  background: rgba(255, 255, 255, 0.7);
  color: rgba(90, 100, 110, 0.9);
}

.body {
  display: block;
}

.panel {
  padding: 24px 26px;
}

.panelHeading {
  margin: 0 0 6px;
  font-family: var(--token-font-display);
  font-size: 20px;
  color: var(--token-text-primary);
}

.panelHeading:not(:first-child) {
  margin-top: 18px;
}

.panelHint {
  margin: 0;
  color: rgba(28, 38, 32, 0.78);
  font-size: 14px;
  line-height: 1.5;
}
